import { useEffect, useState } from "react";
import { Sparkles, ShieldCheck, ArrowRight, RefreshCcw, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";

function relativeTime(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/**
 * Pre-arrival replies inbox — every customer who has completed the
 * concierge form via their pre-call link in the last 24 hours. Fires
 * regardless of web-push permission so the advisor never misses a
 * submission. Collapsed by default when empty; auto-refreshes every 30s.
 */
export default function PreArrivalRepliesInbox({ nav }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/precall/submissions-today");
      setRows(r.data?.submissions || []);
    } catch { /* silent — this panel is best-effort */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  // Nothing to show and initial load is still running → render nothing to
  // avoid a "flash of empty panel" on dashboard mount.
  if (loading && rows.length === 0) return null;
  // Silent when there's nothing today — advisor doesn't need a permanent
  // empty state hogging space on the dashboard.
  if (rows.length === 0) return null;

  return (
    <div className="card-default p-5 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid="advisor-precall-replies-inbox-card">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 text-primary shrink-0" />
          <h2 className="font-display font-bold text-base truncate">Pre-arrival replies</h2>
          <span className="text-[10px] uppercase tracking-wider text-primary font-mono-plex shrink-0">
            {rows.length} in the last 24h
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-muted-foreground hover:text-foreground p-1" title="Refresh" data-testid="precall-replies-refresh">
            <RefreshCcw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-muted-foreground hover:text-foreground p-1"
            title={open ? "Collapse" : "Expand"}
            data-testid="precall-replies-toggle"
          >
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Customers who&rsquo;ve completed their concierge form ahead of the visit — drink, allergies, GDPR and any note now on file.
      </p>

      {open && (
        <ul className="space-y-1.5">
          {rows.slice(0, 8).map((r) => (
            <li
              key={r.job_id}
              onClick={() => nav(`/jobs/${r.job_id}`)}
              className="cursor-pointer flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-primary/20 bg-background/60 hover:bg-primary/5 transition-colors"
              data-testid={`precall-reply-${r.job_id}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="font-semibold text-sm truncate">{r.customer_name || "—"}</span>
                <span className="text-xs text-muted-foreground font-mono-plex shrink-0">{r.vehicle_reg || ""}</span>
                {r.gdpr_marketing_consent && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 px-1.5 py-0.5 text-[10px] shrink-0">
                    <ShieldCheck className="w-2.5 h-2.5" /> mkt
                  </span>
                )}
                {r.gdpr_share_with_oem_consent && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 px-1.5 py-0.5 text-[10px] shrink-0">
                    <ShieldCheck className="w-2.5 h-2.5" /> OEM
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] text-muted-foreground font-mono-plex">{relativeTime(r.submitted_at)}</span>
                <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
